import {
  Card,
  Link,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { ItemAPI } from '../../api/item';
import {
  dateFormats,
  formatDateTime,
  printRoundedNumber,
} from '../../shared/utils';
import { useNotificationContext } from '../shared/notification';
import {
  colors,
  GridContainer,
  CompactTableCell,
  GridItem,
  LabeledText,
  CompactTableRow,
} from '../shared/ui-extension';
import { transactionTypeDescriptions } from './constants';
import { TransactionStatus } from './transaction-status';

export const getTransactionLineNumbers = (t) => {
  const lineNumberRange =
    t.transactionReferenceId && t.transactionReferenceId.split(':')[1];

  if (lineNumberRange.indexOf('-') > 0) {
    const [startRange, endRange] = lineNumberRange.split('-');
    const lineNumbers = [];
    for (let i = +startRange; i <= endRange; i++) {
      lineNumbers.push(i);
    }
    return lineNumbers;
  }

  return [+lineNumberRange];
};

export const TransactionDetails = ({ transaction }) => {
  const [transactions, setTransactions] = useState([]);
  const { handleException } = useNotificationContext();

  useEffect(() => {
    if (transaction.transactionReferenceNumber && transaction.transactionType) {
      loadInventoryTransactions();
    }
  }, [transaction.transactionReferenceNumber]);

  const loadInventoryTransactions = async () => {
    try {
      const { data } = await ItemAPI.getInventoryTransactions({
        transactionReferenceNumber: transaction.transactionReferenceNumber,
        transactionType: transaction.transactionType,
      });
      setTransactions(data);
    } catch (e) {
      handleException(e);
    }
  };

  return (
    <GridContainer spacing={3}>
      <GridItem>
        <LabeledText
          label={
            transactionTypeDescriptions[transaction.transactionType] ||
            transaction.transactionType
          }
          text={transaction.transactionReferenceNumber}
        />
      </GridItem>
      <GridItem>
        <LabeledText label='Account' text={transaction.customerName} />
      </GridItem>
      <GridItem>
        <LabeledText label='Consultant' text={transaction.salespersonName} />
      </GridItem>
      <GridItem>
        <LabeledText label='Supplier' text={transaction.supplierName} />
      </GridItem>
      <GridItem>
        <LabeledText label='Buyer' text={transaction.buyerName} />
      </GridItem>
      <GridItem xs={12}>
        <TableContainer component={Card}>
          <Table size='small'>
            <TableHead
              sx={{
                backgroundColor: colors.lightGray,
              }}
            >
              <TableRow>
                <CompactTableCell>P21 Line #</CompactTableCell>
                <CompactTableCell>Item</CompactTableCell>
                <CompactTableCell>Stock Status</CompactTableCell>
                <CompactTableCell>Ship-from</CompactTableCell>
                <CompactTableCell>Ordered</CompactTableCell>
                <CompactTableCell>Order Date</CompactTableCell>
                <CompactTableCell>Required Date</CompactTableCell>
                <CompactTableCell>Description</CompactTableCell>
                <CompactTableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions
                .filter((t) => getTransactionLineNumbers(t).some((n) => n > 0))
                .sort(
                  (a, b) =>
                    getTransactionLineNumbers(a)?.[0] -
                    getTransactionLineNumbers(b)?.[0]
                )
                .map((t, i) => (
                  <CompactTableRow
                    key={i}
                    sx={
                      t.transactionReferenceId ===
                        transaction.transactionReferenceId && {
                        backgroundColor: colors.rowHighlight,
                      }
                    }
                  >
                    <CompactTableCell>
                      {getTransactionLineNumbers(t).join(', ')}
                    </CompactTableCell>
                    <CompactTableCell>
                      {(t.itemId && t.itemId !== transaction.itemId && (
                        <Link
                          href={`/inventory-management?itemId=${t.itemId}`}
                          target='_blank'
                        >
                          {t.itemId}
                        </Link>
                      )) ||
                        t.itemId}
                    </CompactTableCell>
                    <CompactTableCell>
                      <TransactionStatus transaction={t} />
                    </CompactTableCell>
                    <CompactTableCell>
                      {t.locationName || t.locationId}
                    </CompactTableCell>
                    <CompactTableCell>
                      {printRoundedNumber(t.quantity, 0)}
                    </CompactTableCell>
                    <CompactTableCell>
                      {formatDateTime(t.transactionCreatedAt, dateFormats.date)}
                    </CompactTableCell>
                    <CompactTableCell
                      sx={{
                        color:
                          new Date(t.transactionOriginalRequiredAt) <
                            new Date() && colors.warningHigh,
                      }}
                    >
                      {formatDateTime(
                        t.transactionOriginalRequiredAt,
                        dateFormats.date
                      )}
                    </CompactTableCell>
                    <CompactTableCell>
                      <Typography sx={{ fontSize: 12 }}>
                        {t.itemDescription}
                      </Typography>
                    </CompactTableCell>
                    <CompactTableCell />
                  </CompactTableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </GridItem>
    </GridContainer>
  );
};
